import React from "react";
import { Skeleton } from "@/components/ui/skeleton";

/**
 * Card-grid skeleton — mirrors the dog / litter / marketplace card
 * grids (3 across at lg, 2 at sm, 1 mobile). Use during fetch to give
 * the page real shape instead of a centered "Loading…" string.
 */
export function CardGridSkeleton({ count = 6, withImage = true, testId = "skeleton-grid" }) {
  return (
    <div data-testid={testId} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-[#EAEAEA] rounded-xl overflow-hidden shadow-luxury-light">
          {withImage && <Skeleton className="aspect-[4/3] w-full rounded-none bg-cream" />}
          <div className="p-5 space-y-3">
            <Skeleton className="h-5 w-3/4 bg-cream" />
            <Skeleton className="h-3 w-1/2 bg-cream" />
            <div className="flex gap-2 pt-1">
              <Skeleton className="h-5 w-12 rounded-full bg-cream" />
              <Skeleton className="h-5 w-16 rounded-full bg-cream" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Row-list skeleton — for inbox, transfers, offers, chat threads. Each
 * row shows a circular badge + two text lines.
 */
export function RowListSkeleton({ count = 5, testId = "skeleton-rows" }) {
  return (
    <div data-testid={testId} className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="bg-white border border-[#EAEAEA] rounded-xl p-5 flex items-start gap-4 shadow-luxury-light">
          <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0 bg-cream" />
          <div className="flex-1 space-y-2 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <Skeleton className="h-4 w-1/3 bg-cream" />
              <Skeleton className="h-3 w-16 bg-cream" />
            </div>
            <Skeleton className="h-3 w-full bg-cream" />
            <Skeleton className="h-3 w-2/3 bg-cream" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Two-column dashboard / panel skeleton — used for Dashboard load states.
 */
export function PanelSkeleton({ rows = 3, testId = "skeleton-panel" }) {
  return (
    <div data-testid={testId} className="bg-white border border-[#EAEAEA] rounded-xl p-6 space-y-3 shadow-luxury-light">
      <Skeleton className="h-5 w-1/3 bg-cream" />
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-3 w-full bg-cream" />
      ))}
    </div>
  );
}
